"use client";

import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import {
  Sparkles,
  CheckCircle2,
  FileText,
  AlertCircle,
  Image,
  FileWarning,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface StepDetectProps {
  file: File;
  onDetected: (suggestedType: string | null) => void;
  onDetecting: (detecting: boolean) => void;
}

/**
 * Client-side document type detection based on filename patterns.
 * No API call needed — we use heuristic matching for speed.
 */
function detectTypeFromFilename(filename: string): string | null {
  const lower = filename.toLowerCase();

  // Property
  if (/rent|lease|tenancy|tenant|landlord/i.test(lower)) return "rental";
  if (/sale[_ -]deed|conveyance|registry/i.test(lower)) return "sale_deed";
  if (/noc|no[_ -]objection|society/i.test(lower)) return "society_noc";

  // Employment
  if (/employ|offer[_ -]letter|appointment|joining/i.test(lower))
    return "employment";
  if (/freelance|consult|independent[_ -]contractor/i.test(lower))
    return "freelance";
  if (/nda|non[_ -]disclosure|confidential/i.test(lower)) return "nda";

  // Financial
  if (/loan|mortgage|emi|sanction[_ -]letter/i.test(lower)) return "loan";
  if (/insurance|policy|cover[_ -]note|endowment/i.test(lower))
    return "insurance";
  if (/mutual[_ -]fund|sip|amc|folio|scheme/i.test(lower))
    return "mutual_fund";
  if (/credit[_ -]card|card[_ -]agreement|cardmember/i.test(lower))
    return "credit_card";

  // Business
  if (/partnership|deed[_ -]of[_ -]partnership|partner/i.test(lower))
    return "partnership";
  if (/vendor|service[_ -]agreement|sla|msa|master[_ -]service/i.test(lower))
    return "vendor";
  if (
    /startup|shareholder|sha|ssa|investment|term[_ -]sheet|safe|convertible/i.test(
      lower
    )
  )
    return "startup";

  return null;
}

function getFileQualityTips(file: File) {
  const tips: Array<{ type: "success" | "warning" | "info"; message: string }> =
    [];
  const isImage = file.type.startsWith("image/");
  const isPdf = file.type === "application/pdf";
  const sizeMb = file.size / (1024 * 1024);

  if (isPdf) {
    tips.push({ type: "success", message: "pdfDetected" });
  } else if (isImage) {
    tips.push({ type: "info", message: "imageDetected" });
    if (sizeMb < 0.1) {
      tips.push({ type: "warning", message: "imageTooSmall" });
    }
  }

  if (sizeMb > 8) {
    tips.push({ type: "warning", message: "fileLarge" });
  }

  return tips;
}

export function StepDetect({
  file,
  onDetected,
  onDetecting,
}: StepDetectProps) {
  const t = useTranslations("scan");
  const [detected, setDetected] = useState(false);
  const [suggestedType, setSuggestedType] = useState<string | null>(null);
  const tips = getFileQualityTips(file);

  // Stabilize callbacks with refs to avoid infinite re-render loops
  const onDetectedRef = useRef(onDetected);
  const onDetectingRef = useRef(onDetecting);
  onDetectedRef.current = onDetected;
  onDetectingRef.current = onDetecting;

  useEffect(() => {
    onDetectingRef.current(true);

    // Simulate brief detection delay for UX feel
    const timer = setTimeout(() => {
      const result = detectTypeFromFilename(file.name);
      setSuggestedType(result);
      onDetectedRef.current(result);
      onDetectingRef.current(false);
      setDetected(true);
    }, 800);

    return () => clearTimeout(timer);
  }, [file]);

  return (
    <div className="space-y-4">
      {/* Detection status */}
      <div className="rounded-xl border border-border bg-card p-5 space-y-4">
        <div className="flex items-center gap-3">
          {!detected ? (
            <>
              <div className="h-8 w-8 rounded-full bg-brand/10 flex items-center justify-center">
                <Sparkles
                  size={16}
                  className="text-brand animate-pulse"
                />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">
                  {t("detectAnalyzing")}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t("detectAnalyzingDesc")}
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="h-8 w-8 rounded-full bg-green-50 dark:bg-green-950/20 flex items-center justify-center">
                <CheckCircle2
                  size={16}
                  className="text-green-600 dark:text-green-400"
                />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">
                  {t("detectComplete")}
                </p>
                <p className="text-xs text-muted-foreground">
                  {suggestedType
                    ? t("detectSuggested", {
                        type: suggestedType.replace(/_/g, " "),
                      })
                    : t("detectNoSuggestion")}
                </p>
              </div>
            </>
          )}
        </div>

        {/* File info card */}
        <div className="rounded-lg bg-muted/30 border border-border p-3 flex items-center gap-3">
          {file.type === "application/pdf" ? (
            <FileText size={20} className="text-red-500 shrink-0" />
          ) : (
            <Image size={20} className="text-blue-500 shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-foreground truncate">
              {file.name}
            </p>
            <p className="text-[10px] text-muted-foreground">
              {(file.size / (1024 * 1024)).toFixed(1)} MB •{" "}
              {file.type.split("/")[1]?.toUpperCase()}
            </p>
          </div>
        </div>
      </div>

      {/* Quality tips */}
      {detected && tips.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-foreground">
            {t("qualityTips")}
          </h4>
          {tips.map((tip, i) => (
            <div
              key={i}
              className={cn(
                "flex items-start gap-2.5 rounded-lg border p-3",
                tip.type === "success" &&
                  "bg-green-50/50 dark:bg-green-950/10 border-green-200 dark:border-green-900/30",
                tip.type === "warning" &&
                  "bg-amber-50/50 dark:bg-amber-950/10 border-amber-200 dark:border-amber-900/30",
                tip.type === "info" &&
                  "bg-blue-50/50 dark:bg-blue-950/10 border-blue-200 dark:border-blue-900/30"
              )}
            >
              {tip.type === "success" ? (
                <CheckCircle2
                  size={13}
                  className="text-green-600 dark:text-green-400 shrink-0 mt-0.5"
                />
              ) : tip.type === "warning" ? (
                <AlertCircle
                  size={13}
                  className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5"
                />
              ) : (
                <FileWarning
                  size={13}
                  className="text-blue-600 dark:text-blue-400 shrink-0 mt-0.5"
                />
              )}
              <p className="text-xs text-foreground/80">{t(tip.message)}</p>
            </div>
          ))}
        </div>
      )}

      {/* What to expect */}
      {detected && (
        <div className="rounded-xl border border-border bg-card p-4 space-y-2">
          <h4 className="text-xs font-semibold text-foreground flex items-center gap-1.5">
            <Sparkles size={12} className="text-brand" />
            {t("whatToExpect")}
          </h4>
          <ul className="space-y-1.5">
            {["expectItem1", "expectItem2", "expectItem3", "expectItem4"].map(
              (key) => (
                <li
                  key={key}
                  className="flex items-start gap-2 text-xs text-foreground/70"
                >
                  <span className="text-brand mt-0.5">•</span>
                  <span>{t(key)}</span>
                </li>
              )
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
